/**
 * Teams Service
 *
 * Service layer for interacting with the Teams state.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable, firstValueFrom, Subscription } from 'rxjs';
import { filter, take, timeout, distinctUntilChanged } from 'rxjs/operators';
import { TeamsState, Team, TeamsStateModel } from './teams.state';
import {
  LoadTeams,
  LoadTeamsSuccess,
  LoadTeamsFailure,
  AddTeam,
  UpdateTeam,
  RemoveTeam,
  ClearTeams,
  SetTeamsLoading,
  ClearTeamsError,
  SetTeamProfile,
} from './teams.actions';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { AccountDetailsState } from 'src/app/rxjs-store/account/account-details';
import { PartnerUtilitiesService } from '../../../services/partner/partner-utilities.service';
import { Partner } from 'src/app/models/schema';

/**
 * Teams Service
 */
@Injectable({
  providedIn: 'root',
})
export class TeamsService implements OnDestroy {
  private store: Store = inject(Store);
  private firestoreService: FirestoreService = inject(FirestoreService);
  private partnerUtilitiesService: PartnerUtilitiesService = inject(
    PartnerUtilitiesService
  );

  /**
   * Subscription to account partnerIds changes
   */
  private partnerIdsSubscription?: Subscription;

  /**
   * Observable of the entire teams state
   */
  @Select(TeamsState.getState)
  teamsState$!: Observable<TeamsStateModel>;

  /**
   * Observable of all teams
   */
  @Select(TeamsState.teams)
  teams$!: Observable<Team[]>;

  /**
   * Observable of team IDs
   */
  @Select(TeamsState.teamIds)
  teamIds$!: Observable<string[]>;

  /**
   * Observable of team names
   */
  @Select(TeamsState.teamNames)
  teamNames$!: Observable<string[]>;

  /**
   * Observable of loading state
   */
  @Select(TeamsState.loading)
  loading$!: Observable<boolean>;

  /**
   * Observable of loaded state
   */
  @Select(TeamsState.loaded)
  loaded$!: Observable<boolean>;

  /**
   * Observable of error state
   */
  @Select(TeamsState.error)
  error$!: Observable<string | null>;

  /**
   * Observable of whether user has teams
   */
  @Select(TeamsState.hasTeams)
  hasTeams$!: Observable<boolean>;

  /**
   * Observable of team count
   */
  @Select(TeamsState.teamCount)
  teamCount$!: Observable<number>;

  /**
   * Get all teams (synchronous)
   */
  get teams(): Team[] {
    return this.store.selectSnapshot(TeamsState.teams);
  }

  /**
   * Get team by ID (synchronous)
   */
  getTeam(teamId: string): Team | null {
    return this.store.selectSnapshot(TeamsState.team(teamId));
  }

  /**
   * Get team profile by ID (synchronous)
   *
   * Gets the user's profile for a specific team from the team's _profile property
   *
   * @param teamId - Team ID to get profile for
   * @returns The profile data or null if not set
   */
  getTeamProfile(teamId: string): any | null {
    return this.store.selectSnapshot(TeamsState.teamProfile(teamId));
  }

  /**
   * Get team IDs (synchronous)
   */
  get teamIds(): string[] {
    return this.store.selectSnapshot(TeamsState.teamIds);
  }

  /**
   * Get team names (synchronous)
   */
  get teamNames(): string[] {
    return this.store.selectSnapshot(TeamsState.teamNames);
  }

  /**
   * Get loading state (synchronous)
   */
  get isLoading(): boolean {
    return this.store.selectSnapshot(TeamsState.loading);
  }

  /**
   * Get loaded state (synchronous)
   */
  get isLoaded(): boolean {
    return this.store.selectSnapshot(TeamsState.loaded);
  }

  /**
   * Get error state (synchronous)
   */
  get error(): string | null {
    return this.store.selectSnapshot(TeamsState.error);
  }

  /**
   * Check if user has teams (synchronous)
   */
  get hasTeams(): boolean {
    return this.store.selectSnapshot(TeamsState.hasTeams);
  }

  /**
   * Get team count (synchronous)
   */
  get teamCount(): number {
    return this.store.selectSnapshot(TeamsState.teamCount);
  }

  /**
   * Initialize the service and start listening to partnerIds changes
   *
   * Subscribes to the account's partnerIds and automatically reloads
   * teams whenever the partnerIds array changes.
   */
  initialize(): void {
    // Subscribe to account partnerIds changes
    this.partnerIdsSubscription = this.store
      .select(AccountDetailsState.partnerIds)
      .pipe(
        distinctUntilChanged((prev, curr) => {
          // Compare arrays for deep equality
          if (!prev && !curr) return true;
          if (!prev || !curr) return false;
          if (prev.length !== curr.length) return false;
          return prev.every((id, index) => id === curr[index]);
        })
      )
      .subscribe((partnerIds) => {
        if (partnerIds && partnerIds.length > 0) {
          // Partner IDs exist - reload teams
          this.loadTeams(partnerIds).catch((error) => {
            // Silently handle error - already logged in loadTeams
          });
        } else {
          // No partner IDs - clear teams
          this.clearTeams();
        }
      });
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    if (this.partnerIdsSubscription) {
      this.partnerIdsSubscription.unsubscribe();
    }
  }

  /**
   * Load teams from Firestore (Observable-based)
   *
   * Fetches team data for an array of partner IDs using observable streams.
   * Waits for each document to exist (first non-null value) before resolving.
   * All documents are loaded in parallel for optimal performance.
   *
   * @param partnerIds - Array of partner IDs to load
   * @returns Promise that resolves when all teams are loaded
   */
  async loadTeams(partnerIds: string[]): Promise<Partner[]> {
    // Dispatch load action
    this.store.dispatch(new LoadTeams(partnerIds));

    try {
      if (partnerIds.length === 0) {
        this.store.dispatch(new LoadTeamsSuccess([]));
        return [];
      }

      // Load all team documents in parallel using observables
      const teamPromises = partnerIds.map(async (partnerId) => {
        const docPath = `partners/${partnerId}`;

        try {
          const team = await firstValueFrom(
            this.firestoreService.getDocument$<Team>(docPath).pipe(
              take(1), // Take the first emitted value (null or data)
              timeout(5000) // Reduced timeout - should be fast if document exists
            )
          );

          // If team is null, document doesn't exist - skip it
          if (!team) {
            return [];
          }

          return {
            ...team,
            id: team.id || partnerId, // Ensure ID is set
          };
        } catch (error: any) {
          return [];
        }
      });

      const teamResults = await Promise.all(teamPromises);

      // Filter out null results and augment with computed properties
      const teams: Team[] = teamResults
        .filter((team): team is NonNullable<typeof team> => team !== null)
        .map((team) =>
          this.partnerUtilitiesService.augmentPartner(team as Partner)
        );

      // Dispatch success action
      this.store.dispatch(new LoadTeamsSuccess(teams));
      return teams;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load teams';

      // Dispatch failure action
      this.store.dispatch(new LoadTeamsFailure(errorMessage));

      // Re-throw for caller to handle
      throw error;
    }
  }

  /**
   * Add a single team
   *
   * @param team - Team to add
   */
  addTeam(team: Team): void {
    this.store.dispatch(new AddTeam(team));
  }

  /**
   * Update a team
   *
   * @param teamId - Team ID to update
   * @param updates - Partial team data to update
   */
  updateTeam(teamId: string, updates: Partial<Team>): void {
    this.store.dispatch(new UpdateTeam(teamId, updates));
  }

  /**
   * Remove a team
   *
   * @param teamId - Team ID to remove
   */
  removeTeam(teamId: string): void {
    this.store.dispatch(new RemoveTeam(teamId));
  }

  /**
   * Clear all teams
   */
  clearTeams(): void {
    this.store.dispatch(new ClearTeams());
  }

  /**
   * Clear error
   */
  clearError(): void {
    this.store.dispatch(new ClearTeamsError());
  }

  /**
   * Set team profile
   *
   * Sets the user's profile for a specific team (local only, not persisted)
   *
   * @param teamId - Team ID to set profile for
   * @param profile - Profile data to set
   */
  setTeamProfile(teamId: string, profile: any): void {
    this.store.dispatch(new SetTeamProfile(teamId, profile));
  }
}
